import { useState, useEffect, useCallback, type ReactNode } from 'react';
import type { User, Session, AuthError as SupabaseAuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { AuthContext, type AuthState, type AuthError } from './authTypes';

// Session storage key for preserving tenant through OAuth flow
const TENANT_STORAGE_KEY = 'auth_tenant_slug';

interface AuthProviderProps {
  children: ReactNode;
}

function mapSupabaseError(error: SupabaseAuthError): AuthError {
  return {
    type: 'oauth_error',
    message: error.message,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  // Initialize auth state on mount
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession }, error: sessionError }) => {
      if (sessionError) {
        setError({
          type: 'session_error',
          message: sessionError.message,
        });
      } else {
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setError(null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);

    // Build redirect URL, preserving tenant param for localhost
    let redirectTo = `${window.location.origin}/auth/callback`;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      const tenantParam = new URLSearchParams(window.location.search).get('tenant');
      if (tenantParam) {
        sessionStorage.setItem(TENANT_STORAGE_KEY, tenantParam);
        redirectTo += `?tenant=${encodeURIComponent(tenantParam)}`;
      }
    }

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
      },
    });
    if (oauthError) {
      setError(mapSupabaseError(oauthError));
    }
  }, []);

  const signInWithMicrosoft = useCallback(async () => {
    setError(null);

    // Build redirect URL, preserving tenant param for localhost
    let redirectTo = `${window.location.origin}/auth/callback`;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      const tenantParam = new URLSearchParams(window.location.search).get('tenant');
      if (tenantParam) {
        sessionStorage.setItem(TENANT_STORAGE_KEY, tenantParam);
        redirectTo += `?tenant=${encodeURIComponent(tenantParam)}`;
      }
    }

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo,
        scopes: 'email profile openid',
      },
    });
    if (oauthError) {
      setError(mapSupabaseError(oauthError));
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) {
      setError({
        type: 'unknown',
        message: signOutError.message,
      });
    }
  }, []);

  const value: AuthState = {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
    error,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}

// Export storage key for use in AuthCallback
export { TENANT_STORAGE_KEY };
